import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Switch,
  Alert,
  AlertTitle,
  MenuItem
} from '@mui/material';
import { useData } from '../../context/DataContext';
import { v4 as uuidv4 } from 'uuid';
import { getDoctorAvatar, cleanupImageUrl, getGenderAvatar } from '../../utils/imageUtils';
import RichTextEditor from '../common/RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import DraggableList from '../common/DraggableList';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

// Component to load and display doctor images
const DoctorAvatar = ({ doctor, size = 50 }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    const loadDoctorImage = async () => {
      setImageLoading(true);
      try {
        const avatarUrl = await getDoctorAvatar(doctor);
        setImageUrl(avatarUrl);
      } catch (error) {
        console.error('Error loading doctor image:', error);
        setImageUrl(getGenderAvatar(doctor.gender));
      }
      setImageLoading(false);
    };

    loadDoctorImage();

    // Cleanup function
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        cleanupImageUrl(imageUrl);
      }
    };
  }, [doctor.image, doctor.imageId, doctor.image_id, doctor.gender]);

  return (
    <Avatar
      src={imageUrl || getGenderAvatar(doctor.gender)}
      sx={{ 
        width: size, 
        height: size, 
        backgroundColor: imageLoading ? '#f0f0f0' : 'transparent',
        mx: 'auto' // Center the avatar
      }}
    >
      {doctor.name.charAt(0)}
    </Avatar>
  );
};

const ManageDoctors = () => {
  const { state, dispatch } = useData();
  const { doctors, departments } = state;

  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    education: '',
    experience: '',
    bio: '',
    email: '',
    phone: '',
    gender: 'male',
    imageFile: null,
    imagePreview: null,
    scheduleText: '',
    showExperience: true,
    showEducation: true,
    showEmailPhone: true,
    showScheduleText: true,
    showScheduleTime: false
  });

  // Use departments from database instead of hardcoded specialties
  const specialties = departments && departments.length > 0 
    ? departments.map(dept => dept.name).sort()
    : [
        'Cardiology', 'Neurology', 'Pediatrics', 'Orthopedics', 'Oncology', 
        'Emergency Medicine', 'Internal Medicine', 'Surgery', 'Radiology', 
        'Psychiatry', 'Dermatology', 'Ophthalmology', 'ENT', 'Urology'
      ];

  const handleOpenDialog = (doctor = null) => {
    if (doctor) {
      setEditingDoctor(doctor);
      setFormData({
        name: doctor.name,
        specialty: doctor.specialty,
        education: doctor.education || '',
        experience: doctor.experience || '',
        bio: doctor.bio || '',
        email: doctor.email || '',
        phone: doctor.phone || '',
        gender: doctor.gender || 'male',
        imageFile: null,
        imagePreview: doctor.image || null,
        scheduleText: doctor.scheduleText || '',
        showExperience: doctor.showExperience !== false,
        showEducation: doctor.showEducation !== false,
        showEmailPhone: doctor.showEmailPhone !== false,
        showScheduleText: doctor.showScheduleText !== false,
        showScheduleTime: doctor.showScheduleTime === true
      });
    } else {
      setEditingDoctor(null);
      setFormData({
        name: '',
        specialty: '',
        education: '',
        experience: '',
        bio: '',
        email: '',
        phone: '',
        gender: 'male',
        imageFile: null,
        imagePreview: null,
        scheduleText: '',
        showExperience: true,
        showEducation: true,
        showEmailPhone: true,
        showScheduleText: true,
        showScheduleTime: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    // Clean up preview URL to prevent memory leaks
    if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    setOpenDialog(false);
    setEditingDoctor(null);
  };

  const handleOpenDetailsDialog = (doctor) => {
    setSelectedDoctor(doctor);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedDoctor(null);
  };

  const handleOpenDeleteDialog = (doctor) => {
    setSelectedDoctor(doctor);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedDoctor(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };



  const handleToggleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setFormData({
        ...formData,
        imageFile: file,
        imagePreview: previewUrl
      });
    }
  };


  const handleSubmit = async () => {
    // All fields are now optional, no validation needed

    try {
      const doctorData = {
        name: formData.name,
        specialty: formData.specialty,
        education: formData.education,
        experience: formData.experience,
        bio: formData.bio,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        scheduleText: formData.scheduleText,
        showExperience: formData.showExperience,
        showEducation: formData.showEducation,
        showEmailPhone: formData.showEmailPhone,
        showScheduleText: formData.showScheduleText,
        showScheduleTime: formData.showScheduleTime
      };

      let result;

    if (editingDoctor) {
        result = await dispatch({
        type: 'UPDATE_DOCTOR',
        payload: {
          ...editingDoctor,
            ...doctorData,
            image_id: editingDoctor.imageId || editingDoctor.image_id // Include existing image ID
          },
          imageFile: formData.imageFile
      });
    } else {
        result = await dispatch({
        type: 'ADD_DOCTOR',
        payload: {
          id: uuidv4(),
            ...doctorData
          },
          imageFile: formData.imageFile
        });
      }

      if (result && result.success) {
        // Clean up preview URL
        if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(formData.imagePreview);
        }
        handleCloseDialog();
        alert('Doctor saved successfully!');
      } else {
        alert('Failed to save doctor: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving doctor:', error);
      alert('Failed to save doctor: ' + error.message);
    }
  };

  const handleDelete = () => {
    if (selectedDoctor) {
      dispatch({
        type: 'DELETE_DOCTOR',
        payload: selectedDoctor.id
      });
      handleCloseDeleteDialog();
    }
  };

  const handleReorder = async (reorderedDoctors) => {
    try {
      // Update local state immediately for instant UI feedback
      dispatch({
        type: 'REORDER_DOCTORS',
        payload: reorderedDoctors
      });
      
      // Then update the database in the background
      const updatePromises = reorderedDoctors.map((doctor, index) => 
        dispatch({
          type: 'UPDATE_DOCTOR',
          payload: {
            ...doctor,
            orderIndex: index
          }
        })
      );
      
      await Promise.all(updatePromises);
      console.log('Doctors reordered successfully');
    } catch (error) {
      console.error('Error reordering doctors:', error);
      alert('Failed to save new order. Please try again.');
    }
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Doctors
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Doctor
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Drag to reorder doctors - changes will be reflected on the public website.
      </Typography>

      {/* Search Box */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search doctors by name or specialty..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
      </Paper>

      {/* Draggable Doctors List */}
      <Paper sx={{ p: 2 }}>
        {(() => {
          // Filter doctors based on search query
          const filteredDoctors = searchQuery.trim() === ''
            ? doctors
            : doctors.filter(doctor =>
                doctor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                doctor.specialty?.toLowerCase().includes(searchQuery.toLowerCase())
              );

          return (
            <>
              <Typography variant="h6" gutterBottom>
                Doctors List ({filteredDoctors.length} of {doctors.length})
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {searchQuery.trim() === '' ? 'Drag to reorder' : 'Clear search to enable reordering'}
                </Typography>
              </Typography>
              
              <DraggableList
                items={filteredDoctors}
                onReorder={searchQuery.trim() === '' ? handleReorder : () => {}}
          renderItem={(doctor, index, isDragging) => (
            <Paper 
              sx={{ 
                p: 2, 
                mb: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={() => handleOpenDetailsDialog(doctor)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 2 }}>
                  <DoctorAvatar doctor={doctor} size={50} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {doctor.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {doctor.specialty}
                  </Typography>
                </Box>
                <Chip label={doctor.specialty} color="primary" size="small" sx={{ mr: 2 }} />
                <Box onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(doctor)}
                    color="primary"
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDeleteDialog(doctor)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          )}
        />
            </>
          );
        })()}
      </Paper>

      {/* Doctor Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Doctor Details
        </DialogTitle>
        <DialogContent>
          {selectedDoctor && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <DoctorAvatar doctor={selectedDoctor} size={120} />
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
                    {selectedDoctor.name}
                  </Typography>
                  <Chip label={selectedDoctor.specialty} color="primary" size="large" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Professional Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      <strong>Education:</strong> {selectedDoctor.education}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      <strong>Experience:</strong> {selectedDoctor.experience}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      {selectedDoctor.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      {selectedDoctor.phone}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Biography
                  </Typography>
                  <Typography variant="body1" sx={{ textAlign: 'justify' }}>
                    {selectedDoctor.bio}
                  </Typography>
                </Box>

                {selectedDoctor.scheduleText && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Schedule
                    </Typography>
                    <Typography 
                      variant="body1" 
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedDoctor.scheduleText) }}
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
          <Button 
            onClick={() => {
              handleCloseDetailsDialog();
              handleOpenDialog(selectedDoctor);
            }} 
            variant="contained"
            startIcon={<EditIcon />}
          >
            Edit Doctor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Warning</AlertTitle>
            This action cannot be undone. The doctor will be permanently removed from the system.
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{selectedDoctor?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete Doctor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Doctor Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specialty"
                name="specialty"
                value={formData.specialty}
                onChange={handleInputChange}
                select
                helperText={departments && departments.length > 0 
                  ? "Select from available departments" 
                  : "Loading departments..."}
              >
                {specialties.map(specialty => (
                  <MenuItem key={specialty} value={specialty}>{specialty}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Education"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                placeholder="e.g., Harvard Medical School"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                placeholder="e.g., 15 years"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                select
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Profile Image
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="doctor-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="doctor-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Upload Image
                  </Button>
                </label>
                {formData.imageFile && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Selected: {formData.imageFile.name} ({Math.round(formData.imageFile.size / 1024)}KB)
                  </Typography>
                )}
                {formData.imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={formData.imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                label="Bio"
                value={formData.bio}
                onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
                placeholder="Brief biography and expertise..."
                rows={4}
                helperText="Use the toolbar above to format your text with bold, italic, lists, and more"
              />
            </Grid>
            
            {/* Schedule Text Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Practice Schedule
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <RichTextEditor
                label="Schedule"
                value={formData.scheduleText}
                onChange={(value) => setFormData(prev => ({ ...prev, scheduleText: value }))}
                placeholder="Enter doctor's schedule in text format (e.g., Monday-Friday: 9:00 AM - 5:00 PM)"
                rows={3}
                helperText="Use the toolbar above to format the schedule text with bold, italic, lists, and more"
              />
            </Grid>

            {/* Visibility Toggles Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Visibility Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Control which information is displayed on the doctor's public profile
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showExperience}
                    onChange={handleToggleChange('showExperience')}
                    color="primary"
                  />
                }
                label="Show Experience"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showEducation}
                    onChange={handleToggleChange('showEducation')}
                    color="primary"
                  />
                }
                label="Show Education"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showEmailPhone}
                    onChange={handleToggleChange('showEmailPhone')}
                    color="primary"
                  />
                }
                label="Show Email & Phone"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showScheduleText}
                    onChange={handleToggleChange('showScheduleText')}
                    color="primary"
                  />
                }
                label="Show Schedule Text"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showScheduleTime}
                    onChange={handleToggleChange('showScheduleTime')}
                    color="primary"
                  />
                }
                label="Show Schedule Time Format"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingDoctor ? 'Update Doctor' : 'Add Doctor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDoctors; 